import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { BookingModalProvider } from './contexts/BookingModalContext';
import Lenis from '@studio-freight/lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

import Header from './components/Header';
import { CrisisDrawer } from './components/CrisisDrawer';
import { BookingModal } from './components/BookingModal';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { OurTeamPage } from './pages/OurTeamPage';
import { ServicesPage } from './pages/ServicesPage';
import { DrugAlcoholServicesPage } from './pages/DrugAlcoholServicesPage';
import { OutpatientPage } from './pages/OutpatientPage';
import { OutreachPage } from './pages/OutreachPage';

import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { ContactPage } from './pages/ContactPage';
import { HealingWithinReachPage } from './pages/HealingWithinReachPage';

import { SpeedInsights } from '@vercel/speed-insights/react';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // Lenis smooth scroll setup
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <ThemeProvider>
      <BookingModalProvider>
        <HashRouter>
          <SpeedInsights />
          <ScrollToTop />
          <div className="min-h-screen flex flex-col bg-mscg-light-gray text-mscg-dark-gray font-sans">
            <Header />
            <CrisisDrawer />
            <BookingModal />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/our-team" element={<OurTeamPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/drug-alcohol" element={<DrugAlcoholServicesPage />} />
                <Route path="/services/outpatient" element={<OutpatientPage />} />
                <Route path="/outreach" element={<OutreachPage />} />

                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/healing-within-reach" element={<HealingWithinReachPage />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </HashRouter>
      </BookingModalProvider>
    </ThemeProvider>
  );
};

export default App;
